import { useQueryClient } from "@tanstack/react-query";
import { Lock } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, FileText, FolderArchive, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { catColor, catLabel } from "@/lib/lore";
import { SortableList } from "@/components/SortableList";
import { GroupBySelect, GroupTree } from "@/components/export/GroupTree";
import { ThemePicker } from "@/components/export/ThemePicker";
import { ThemePreview } from "@/components/export/ThemePreview";

export async function downloadExport(uid, kind) {
  const r = await api.get(`/universes/${uid}/export/${kind}`, { responseType: "blob" });
  const name = (r.headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] || `lore.${{ pdf: "pdf", epub: "epub" }[kind] || "zip"}`;
  const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(r.data), download: name });
  a.click();
  URL.revokeObjectURL(a.href);
}

export function ExportDialog({ uid, canEdit, onOpenChange }) {
  const feats = useQueryClient().getQueryData(["universe", uid])?.features;
  const has = (f) => !feats || feats.includes(f);
  const isPro = has("timelines") && has("maps") && has("relationships");
  const [layout, setLayout] = useState(null);
  const [options, setOptions] = useState({});
  const [preview, setPreview] = useState([]);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState("");
  const [withImages, setWithImages] = useState(false);
  const [withCals, setWithCals] = useState(true);
  const [withRels, setWithRels] = useState(true);
  const [withRecipes, setWithRecipes] = useState(true);
  const [recipeCount, setRecipeCount] = useState(0);
  const [themes, setThemes] = useState([]);
  const [theme, setTheme] = useState("parchment");
  const [suggested, setSuggested] = useState(null);

  useEffect(() => {
    api.get(`/universes/${uid}/export/layout`).then(({ data }) => {
      setLayout(data.layout); setOptions(data.options); setPreview(data.preview); setSel(data.layout[0]?.name || null); setWithImages(!!data.include_images); setWithCals(data.include_calendars !== false); setWithRels(data.include_relationships !== false); setWithRecipes(data.include_recipes !== false); setRecipeCount(data.recipe_count || 0);
      setThemes(data.themes || []); setSuggested(data.suggested_theme); setTheme(data.theme || data.suggested_theme || "parchment");
    }).catch((e) => toast.error(errMsg(e)));
  }, [uid]);

  const updateCat = async (name, patch, refresh = false) => {
    const next = layout.map((c) => (c.name === name ? { ...c, ...patch } : c));
    setLayout(next);
    if (refresh) {
      const { data } = await api.post(`/universes/${uid}/export/preview`, { categories: next });
      setPreview(data.preview);
    }
  };
  const suggest = async () => {
    setBusy("ai");
    try {
      const { data } = await api.post(`/universes/${uid}/export/suggest`);
      setLayout(layout.map((c) => ({ ...c, group_by: data.layout.find((x) => x.name === c.name)?.group_by || [], group_order: [] })));
      setPreview(data.preview);
      toast.success("Arrangement suggested. Review it, then export.");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };
  const run = async (kind) => {
    setBusy(kind);
    const t = toast.loading({ pdf: "Binding your Lore Bible…", epub: "Typesetting your e-book…", save: "Saving arrangement…" }[kind] || "Packing Markdown files…");
    try {
      if (canEdit) await api.put(`/universes/${uid}/export/layout`, { categories: layout, include_images: withImages, include_calendars: withCals, include_relationships: withRels, include_recipes: withRecipes, theme });
      if (kind !== "save") await downloadExport(uid, kind);
      toast.success(kind === "save" ? "Arrangement saved" : "Export ready", { id: t });
    } catch (e) {
      toast.error(errMsg(e), { id: t });
    } finally {
      setBusy("");
    }
  };

  const cat = layout?.find((c) => c.name === sel);
  const sec = preview.find((s) => s.category === sel);
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="export-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl">Arrange your Lore Bible</DialogTitle>
          <DialogDescription>Choose which categories to include and how to group them. Drag to reorder categories, groups and entries. Your arrangement is saved for next time.</DialogDescription>
        </DialogHeader>
        {!layout ? <p className="py-10 text-sm text-[#A8A090]">Gathering your universe…</p> : (
          <div className="grid min-h-0 flex-1 gap-5 md:grid-cols-[240px_1fr]">
            <div className="min-h-0 overflow-y-auto lf-scroll pr-1">
              <div className="lf-label mb-2">Categories</div>
              <SortableList scope="cats" items={layout} getKey={(c) => c.name} onReorder={setLayout} testid="export-category-list" render={(c) => (
                <div className={`flex items-center gap-2 rounded px-2 py-1.5 text-sm cursor-pointer ${sel === c.name ? "bg-[#1A202C] text-[#F3EBDD]" : "text-[#A8A090]"}`} onClick={() => setSel(c.name)} data-testid={`export-category-${c.name}`}>
                  <Checkbox checked={c.include} onClick={(e) => e.stopPropagation()} onCheckedChange={(v) => updateCat(c.name, { include: !!v })} data-testid={`export-include-${c.name}`} />
                  <span className="h-2 w-2 rounded-full" style={{ background: catColor(c.name) }} />
                  <span className={c.include ? "" : "line-through opacity-60"}>{catLabel(c.name)}</span>
                </div>
              )} />
            </div>
            <div className="min-h-0 overflow-y-auto lf-scroll pr-1">
              {cat && (
                <>
                  <div className="mb-4 flex flex-wrap items-end gap-3">
                    <GroupBySelect label="Group by" value={cat.group_by[0]} options={options[cat.name] || []} testid="export-group-by-1"
                      onChange={(v) => updateCat(cat.name, { group_by: v ? [v] : [], group_order: [] }, true)} />
                    {cat.group_by[0] && (
                      <GroupBySelect label="Then by" value={cat.group_by[1]} options={(options[cat.name] || []).filter((o) => o.key !== cat.group_by[0])} testid="export-group-by-2"
                        onChange={(v) => updateCat(cat.name, { group_by: v ? [cat.group_by[0], v] : [cat.group_by[0]], group_order: [] }, true)} />
                    )}
                  </div>
                  {sec && <GroupTree sec={sec} onChange={(s, patch) => { setPreview(preview.map((x) => (x.category === s.category ? s : x))); updateCat(cat.name, patch); }} />}
                </>
              )}
            </div>
          </div>
        )}
        {canEdit && themes.length > 0 && (
          <div className="border-t border-[#2D3442] pt-3">
            <div className="mb-2 text-sm text-[#E8DFD1]">PDF theme <span className="text-xs text-[#A8A090]">· the e-book stays plain so readers can use their own font and night mode</span></div>
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0"><ThemePicker themes={themes} value={theme} suggested={suggested} onChange={setTheme} /></div>
              <ThemePreview uid={uid} theme={theme} layout={layout} includeCalendars={withCals} />
            </div>
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-[#2D3442] pt-4">
          {canEdit && (
            <label className="flex items-center gap-2 text-sm text-[#E8DFD1]" title="Cover image beside each info box, up to 4 more per entry, and timeline images in the chronology">
              <Switch checked={withImages} onCheckedChange={setWithImages} data-testid="export-include-images" />Include images in PDF
            </label>
          )}
          {!isPro && <span className="flex items-center gap-1.5 text-xs text-[#A8A090]" data-testid="export-free-note"><Lock className="h-3.5 w-3.5 text-[#FBBF24]" />Sections not in this universe's plan are left out of the export</span>}
          {canEdit && has("timelines") && (
            <label className="flex items-center gap-2 text-sm text-[#E8DFD1]" title="A page per calendar: months, weeks, moons and holidays (Earth lists only holidays you added or edited)">
              <Switch checked={withCals} onCheckedChange={setWithCals} data-testid="export-include-calendars" />Include calendars
            </label>
          )}
          {canEdit && has("relationships") && (
            <label className="flex items-center gap-2 text-sm text-[#E8DFD1]" title="A diagram of every relationship, plus one family-tree page per family">
              <Switch checked={withRels} onCheckedChange={setWithRels} data-testid="export-include-relationships" />Include relationship web & family trees
            </label>
          )}
          {canEdit && recipeCount > 0 && (
            <label className="flex items-center gap-2 text-sm text-[#E8DFD1]" title="A Recipes chapter with ingredients, method and any health stats you've already worked out">
              <Switch checked={withRecipes} onCheckedChange={setWithRecipes} data-testid="export-include-recipes" />Include recipes ({recipeCount})
            </label>
          )}
          {canEdit ? <Button variant="ghost" onClick={suggest} disabled={!!busy || !layout} data-testid="export-suggest-button"><Sparkles className="h-4 w-4 mr-1.5" />{busy === "ai" ? "Thinking…" : "Suggest arrangement"}</Button> : <span />}
          <div className="flex gap-2">
            {canEdit && <Button variant="ghost" onClick={() => run("save")} disabled={!!busy || !layout} data-testid="export-save-layout">Save arrangement</Button>}
            <Button variant="outline" onClick={() => run("epub")} disabled={!!busy || !layout} data-testid="export-epub"><BookOpen className="h-4 w-4 mr-1.5" />{busy === "epub" ? "Typesetting…" : "E-book (.epub)"}</Button>
            <Button variant="outline" onClick={() => run("markdown")} disabled={!!busy || !layout} data-testid="export-markdown"><FolderArchive className="h-4 w-4 mr-1.5" />Markdown (.zip)</Button>
            <Button onClick={() => run("pdf")} disabled={!!busy || !layout} data-testid="export-pdf"><FileText className="h-4 w-4 mr-1.5" />{busy === "pdf" ? "Binding…" : "Export PDF"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
